import { AlertColor, Box, CircularProgress } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar/UPSnackbar';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import GeneralConfirmModal from 'components/general-confirm-modal';
import { UPTanStackTable } from 'components/tanstack-table';
import WarningBar from 'components/warning-bar';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__CONFIRM_SAVE,
  CONTRACT_COMPLIANCE__NO_MATCHES,
  CONTRACT_COMPLIANCE__PLANNING_ADD_OVERRIDE,
  CONTRACT_COMPLIANCE__PLANNING_FAIL_CLOSED,
  CONTRACT_COMPLIANCE__PLANNING_INTRO,
  CONTRACT_COMPLIANCE__PLANNING_NO_OVERRIDES,
  CONTRACT_COMPLIANCE__PLANNING_ROLE_DEFAULTS,
  CONTRACT_COMPLIANCE__PLANNING_SEARCH_API_USER,
  CONTRACT_COMPLIANCE__PLANNING_USER_OVERRIDES,
  CONTRACT_COMPLIANCE__REMOVE_DISCLAIMER,
  GENERAL__RETRY,
} from 'translations/constants';
import { IPlanningInThePastEntry } from 'types/contract-compliance/IContractCompliance';
import { PAGE_SIZE_OPTIONS } from '../constants';
import {
  CardHeader,
  CardTitle,
  HintText,
  PanelErrorRow,
  RuleCardContainer,
  SearchWrapper,
  SubAreaContainer,
  ToolbarRow,
} from '../styles';
import { buildOverrideColumns, buildRoleDefaultColumns } from './columns';
import { ROLE_SLUGS, TEST_ID } from './constants';
import { useOverrides } from './hooks/useOverrides';
import { usePlanningInThePastDefaults } from './hooks/usePlanningInThePastDefaults';
import { usePlanningInThePastPanel } from './hooks/usePlanningInThePastPanel';
import PlanningInThePastPanel from './PlanningInThePastPanel';

interface Toast {
  id: number;
  type: AlertColor;
  content: ReactNode;
}

const getRoleRowId = (row: IPlanningInThePastEntry) => row.role;
const getOverrideRowId = (row: IPlanningInThePastEntry) => row.id;

/**
 * "Planning in the past" section: which roles, and which API users on which public APIs, may create contracts that
 * start in the past, per worker class. A Role default table plus a searchable, paged list of per-user overrides.
 */
const PlanningInThePast = (): JSX.Element => {
  const [t] = useTranslation();
  const defaults = usePlanningInThePastDefaults();
  const overrides = useOverrides();

  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextToastId = useRef(0);
  const pushToast = useCallback((type: AlertColor, content: ReactNode): number => {
    nextToastId.current += 1;
    const id = nextToastId.current;
    setToasts(previous => [...previous, { id, type, content }]);
    return id;
  }, []);
  const dismissToast = useCallback(
    (id: number) => setToasts(previous => previous.filter(toast => toast.id !== id)),
    [],
  );

  const { reload: reloadDefaults } = defaults;
  const { reload: reloadOverrides } = overrides;
  /** The section reloads with the latest data whenever the panel is saved or closed (side panel pattern). */
  const reloadAll = useCallback(() => {
    reloadDefaults();
    reloadOverrides();
  }, [reloadDefaults, reloadOverrides]);

  const panel = usePlanningInThePastPanel({
    onSaved: (messageKey: string) => {
      reloadAll();
      pushToast('success', t(messageKey));
    },
    onClosed: reloadAll,
    onGone: (message: string) => {
      reloadAll();
      pushToast('error', message);
    },
    onStale: reloadAll,
    onError: (message: string) => pushToast('error', message),
    onRemoveFailed: (message: string, retry: () => void) => {
      // The banner goes away on Retry; a new one is shown if the retry fails too.
      let toastId = 0;
      const onRetry = () => {
        dismissToast(toastId);
        retry();
      };
      toastId = pushToast(
        'error',
        <Box display="flex" alignItems="center" gap="12px">
          <span>{message}</span>
          <UPButton text={t(GENERAL__RETRY)} onClick={onRetry} outlined testId={`${TEST_ID}-remove-retry`} />
        </Box>,
      );
    },
  });

  const { openRole, openAdd, openEdit } = panel;
  const onEditRole = useCallback((row: IPlanningInThePastEntry) => openRole(row.role), [openRole]);
  const onEditOverride = useCallback((row: IPlanningInThePastEntry) => openEdit(row), [openEdit]);
  const roleColumns = useMemo(() => buildRoleDefaultColumns(t, onEditRole), [t, onEditRole]);
  const overrideColumns = useMemo(() => buildOverrideColumns(t, onEditOverride), [t, onEditOverride]);

  const renderRoleDefaults = () => {
    if (defaults.loadErrorMessage && !defaults.hasLoaded) {
      return (
        <PanelErrorRow data-testid={`${TEST_ID}-role-load-error`}>
          <ErrorBar message={defaults.loadErrorMessage} />
          <UPButton text={t(GENERAL__RETRY)} onClick={defaults.reload} outlined testId={`${TEST_ID}-role-load-retry`} />
        </PanelErrorRow>
      );
    }
    if (!defaults.hasLoaded) return <CircularProgress data-testid={`${TEST_ID}-role-loading`} />;
    return (
      <UPTanStackTable
        data={defaults.defaults}
        columns={roleColumns}
        getRowId={getRoleRowId}
        getRowTestId={row => `${TEST_ID}-role-row-${ROLE_SLUGS[row.role]}`}
        testId={`${TEST_ID}-role-table`}
        // A fixed short list of three roles: neither search nor paging.
        showPaginationControls={false}
        enableSorting={false}
        sx={{ height: 'auto' }}
      />
    );
  };

  const renderOverrides = () => {
    if (overrides.loadErrorMessage) {
      return (
        <PanelErrorRow data-testid={`${TEST_ID}-override-load-error`}>
          <ErrorBar message={overrides.loadErrorMessage} />
          <UPButton
            text={t(GENERAL__RETRY)}
            onClick={overrides.reload}
            outlined
            testId={`${TEST_ID}-override-load-retry`}
          />
        </PanelErrorRow>
      );
    }
    if (!overrides.hasLoaded) return <CircularProgress data-testid={`${TEST_ID}-override-loading`} />;
    return (
      <UPTanStackTable
        data={overrides.rows}
        columns={overrideColumns}
        getRowId={getOverrideRowId}
        getRowTestId={row => `${TEST_ID}-override-row-${row.id}`}
        testId={`${TEST_ID}-override-table`}
        emptyMessage={t(
          overrides.debouncedSearch.trim()
            ? CONTRACT_COMPLIANCE__NO_MATCHES
            : CONTRACT_COMPLIANCE__PLANNING_NO_OVERRIDES,
        )}
        manualPagination
        pageCount={overrides.totalPages}
        totalItems={overrides.numberOfResults}
        pagination={overrides.pagination}
        onPaginationChange={overrides.setPagination}
        autoResetPageIndex={false}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        enableSorting={false}
        sx={{ height: 'auto' }}
      />
    );
  };

  return (
    <SubAreaContainer data-testid={TEST_ID}>
      <HintText data-testid={`${TEST_ID}-intro`}>{t(CONTRACT_COMPLIANCE__PLANNING_INTRO)}</HintText>
      <div data-testid={`${TEST_ID}-fail-closed`}>
        <WarningBar message={t(CONTRACT_COMPLIANCE__PLANNING_FAIL_CLOSED)} variant="warning" />
      </div>

      <RuleCardContainer data-testid={`${TEST_ID}-role-card`}>
        <CardTitle>{t(CONTRACT_COMPLIANCE__PLANNING_ROLE_DEFAULTS)}</CardTitle>
        {renderRoleDefaults()}
      </RuleCardContainer>

      <RuleCardContainer data-testid={`${TEST_ID}-override-card`}>
        <CardHeader>
          <CardTitle>{t(CONTRACT_COMPLIANCE__PLANNING_USER_OVERRIDES)}</CardTitle>
          <UPButton
            text={t(CONTRACT_COMPLIANCE__PLANNING_ADD_OVERRIDE)}
            onClick={openAdd}
            testId={`${TEST_ID}-add-override`}
          />
        </CardHeader>
        <ToolbarRow>
          <SearchWrapper>
            <CustomSearchBox
              onSearch={overrides.onSearch}
              placeholder={t(CONTRACT_COMPLIANCE__PLANNING_SEARCH_API_USER)}
              testId={`${TEST_ID}-search`}
            />
          </SearchWrapper>
        </ToolbarRow>
        {renderOverrides()}
      </RuleCardContainer>

      <PlanningInThePastPanel panel={panel} />

      {/* Static text: no lookup of affected contracts. Cancel returns to the panel with the values kept. */}
      <GeneralConfirmModal
        open={panel.isConfirmOpen}
        onClose={panel.cancelConfirm}
        onAction={panel.confirmSave}
        content={t(CONTRACT_COMPLIANCE__CONFIRM_SAVE)}
        confirmTestId={`${TEST_ID}-confirm-save`}
        cancelTestId={`${TEST_ID}-confirm-cancel`}
        actionDisabled={panel.isSaving}
      />
      <GeneralConfirmModal
        open={panel.isRemoveOpen}
        onClose={panel.cancelRemove}
        onAction={panel.confirmRemove}
        content={t(CONTRACT_COMPLIANCE__REMOVE_DISCLAIMER, { entity: panel.entry?.apiUserName ?? '' })}
        confirmTestId={`${TEST_ID}-remove-confirm`}
        cancelTestId={`${TEST_ID}-remove-cancel`}
        actionDisabled={panel.isSaving}
      />

      {toasts.map((toast, index) => (
        <UPSnackBar
          key={toast.id}
          open
          type={toast.type}
          content={toast.content}
          onClose={() => dismissToast(toast.id)}
          vertical="bottom"
          horizontal="right"
          index={index}
        />
      ))}
    </SubAreaContainer>
  );
};

export default PlanningInThePast;
